import React, { useState } from 'react';
import { motion } from 'framer-motion';

const SampleReportSection = () => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="py-20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.h2 
            className="text-3xl font-bold text-midnight-navy mb-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            Professional Research Reports
          </motion.h2>
          <motion.p 
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            transition={{ delay: 0.1 }}
          >
            Generate comprehensive, professional-quality equity research reports with just one click.
          </motion.p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
          <div>
            <h3 className="text-2xl font-bold text-midnight-navy mb-4">Sample Report: J D Wetherspoon plc</h3>
            <p className="text-gray-600 mb-6">
              This sample report demonstrates the depth and quality of analysis that Research Genie provides. Each report includes:
            </p>
            
            <ul className="space-y-4 mb-8">
              <li className="flex items-start">
                <div className="bg-pale-gold bg-opacity-20 rounded-full p-1 mr-3 mt-0.5">
                  <svg className="h-5 w-5 text-pale-gold" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-midnight-navy">Executive Summary</h4>
                  <p className="text-gray-600">Concise overview of key findings and investment recommendations</p>
                </div>
              </li>
              
              <li className="flex items-start">
                <div className="bg-pale-gold bg-opacity-20 rounded-full p-1 mr-3 mt-0.5">
                  <svg className="h-5 w-5 text-pale-gold" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-midnight-navy">SCQA Analysis</h4>
                  <p className="text-gray-600">Detailed breakdown of the situation, complications, questions, and answers</p>
                </div>
              </li>
              
              <li className="flex items-start">
                <div className="bg-pale-gold bg-opacity-20 rounded-full p-1 mr-3 mt-0.5">
                  <svg className="h-5 w-5 text-pale-gold" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-midnight-navy">Financial Analysis</h4>
                  <p className="text-gray-600">Key financial metrics, trends, and comparative analysis</p>
                </div>
              </li>
              
              <li className="flex items-start">
                <div className="bg-pale-gold bg-opacity-20 rounded-full p-1 mr-3 mt-0.5">
                  <svg className="h-5 w-5 text-pale-gold" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-midnight-navy">Risk Assessment</h4>
                  <p className="text-gray-600">Comprehensive analysis of potential risks and mitigating factors</p>
                </div>
              </li>
              
              <li className="flex items-start">
                <div className="bg-pale-gold bg-opacity-20 rounded-full p-1 mr-3 mt-0.5">
                  <svg className="h-5 w-5 text-pale-gold" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-midnight-navy">Investment Conclusion</h4>
                  <p className="text-gray-600">Clear investment recommendation with supporting rationale</p>
                </div>
              </li>
            </ul>
            
            <a 
              href="https://equity-research-analyst-611846019618.us-central1.run.app/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-pale-gold text-midnight-navy px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors inline-flex items-center"
            >
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Generate Your Own Report
            </a>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Report Preview Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                className={`px-4 py-3 text-sm font-medium ${
                  activeTab === 'overview' 
                    ? 'text-electric-blue border-b-2 border-electric-blue' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              <button
                className={`px-4 py-3 text-sm font-medium ${
                  activeTab === 'situation' 
                    ? 'text-electric-blue border-b-2 border-electric-blue' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('situation')}
              >
                Situation
              </button>
              <button
                className={`px-4 py-3 text-sm font-medium ${
                  activeTab === 'complications' 
                    ? 'text-electric-blue border-b-2 border-electric-blue' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('complications')}
              >
                Complications
              </button>
              <button
                className={`px-4 py-3 text-sm font-medium ${
                  activeTab === 'conclusion' 
                    ? 'text-electric-blue border-b-2 border-electric-blue' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('conclusion')}
              >
                Conclusion
              </button>
            </div>
            
            {/* Report Content */}
            <div className="p-6">
              {activeTab === 'overview' && (
                <div>
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-full mr-4"></div>
                    <div>
                      <h3 className="font-bold text-lg">J D Wetherspoon plc (JDW.L)</h3>
                      <p className="text-gray-500">London Stock Exchange</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Current Price</p>
                      <p className="font-bold text-xl">£631.70</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Market Cap</p>
                      <p className="font-bold text-xl">£690.26M</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">P/E Ratio</p>
                      <p className="font-bold text-xl">12.37</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Revenue Growth</p>
                      <p className="font-bold text-xl">5.74%</p>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-midnight-navy mb-2">Executive Summary</h4>
                    <p className="text-gray-700 mb-3">
                      This report analyzes J D Wetherspoon plc (JDW.L), focusing on the potential impact of global supply chain disruptions on the company's operational costs, customer satisfaction, and market valuation.
                    </p>
                    <p className="text-gray-700">
                      Our analysis indicates that while JD Wetherspoon faces significant challenges from supply chain disruptions, the company's strong market position and cost management strategies position it to navigate these challenges effectively. However, investors should monitor the company's ability to maintain service quality and manage cost pressures.
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-gray-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm text-gray-500">Generated on April 26, 2025</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-gray-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm text-gray-500">12 pages</span>
                    </div>
                  </div>
                </div>
              )}
              
              {activeTab === 'situation' && (
                <div>
                  <h3 className="font-bold text-lg text-midnight-navy mb-4">Situation Analysis</h3>
                  
                  <p className="text-gray-700 mb-4">
                    J D Wetherspoon plc (JDW.L), a key player in the consumer cyclical sector, operates within the restaurant industry. With a market capitalization of £690.26M, the company is a significant entity in the UK and Republic of Ireland's hospitality landscape. Currently priced at £631.70, the stock has experienced a 12.71% decline over the past year, fluctuating between £518.5 and £812.36. This volatility reflects the broader challenges faced by the industry, including economic pressures and changing consumer behaviors post-pandemic.
                  </p>
                  
                  <p className="text-gray-700 mb-4">
                    The company reported a revenue growth of 5.74%, indicative of its resilience and capability to navigate a challenging market environment. However, with a profit margin of 2.4%, profitability remains constrained, potentially due to high operational costs and competitive pricing strategies typical in the pub and hospitality sector. The price-to-earnings (P/E) ratio of 12.37 suggests the stock is reasonably valued relative to its earnings, positioning it as a potentially attractive option for value investors seeking exposure to the UK hospitality market.
                  </p>
                  
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <h4 className="font-semibold text-midnight-navy mb-2">Key Financial Indicators</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Revenue</p>
                        <p className="font-medium">£1.82B</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">EBITDA Margin</p>
                        <p className="font-medium">11.3%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Net Profit Margin</p>
                        <p className="font-medium">2.4%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Debt-to-Equity</p>
                        <p className="font-medium">1.24</p>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-700">
                    JD Wetherspoon's financial structure can be further scrutinized by examining its debt-to-equity ratio, which provides insight into its leverage and financial risk. The recent news sentiment surrounding the company has been neutral, reflecting a balanced mix of challenges and opportunities in the current market environment.
                  </p>
                </div>
              )}
              
              {activeTab === 'complications' && (
                <div>
                  <h3 className="font-bold text-lg text-midnight-navy mb-4">Complications Analysis</h3>
                  
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg mb-6">
                    <h4 className="font-semibold text-midnight-navy mb-2">Supply Chain Disruptions</h4>
                    <p className="text-gray-700">
                      The global supply chain crisis is a significant risk for JD Wetherspoon. Disruptions could impact the company's ability to source key inputs, leading to operational inefficiencies, potential service disruptions, and increased costs. These challenges could negatively impact the company's financial performance and subsequently its share price.
                    </p>
                  </div>
                  
                  <h4 className="font-semibold text-midnight-navy mb-3">Key Question:</h4>
                  <div className="bg-gray-50 p-4 rounded-lg mb-6">
                    <p className="text-gray-700 italic">
                      How might global supply chain disruptions increase operational costs for JD Wetherspoon, potentially impacting customer satisfaction, revenue generation, and ultimately the company's market valuation in the medium to long term?
                    </p>
                  </div>
                  
                  <h4 className="font-semibold text-midnight-navy mb-3">Hypotheses:</h4>
                  <div className="space-y-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h5 className="font-medium text-midnight-navy mb-2">"Increased Operational Costs Lead to Price Hikes"</h5>
                      <p className="text-gray-700 text-sm">
                        JD Wetherspoon, due to supply chain disruptions, may face increased operational costs. To maintain profitability, the company might choose to pass these costs onto consumers by increasing prices. However, this could lead to customer dissatisfaction and potentially reduce patronage, impacting revenue negatively. The resulting decrease in revenues could then lead to a downward revision of its market valuation by investors, thus reducing the share price.
                      </p>
                    </div>
                    
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h5 className="font-medium text-midnight-navy mb-2">"Operational Disruptions Cause Service Quality Decline"</h5>
                      <p className="text-gray-700 text-sm">
                        Supply chain disruptions could lead to shortages of key products, impacting JD Wetherspoon's ability to offer a full range of services. This could lead to a decline in service quality, leading to customer dissatisfaction and potential revenue loss. If this persists, it could impact the company's net income and EBITDA margins, leading to a decrease in share price due to lowered investor confidence in the company's ability to manage its operations effectively.
                      </p>
                    </div>
                    
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h5 className="font-medium text-midnight-navy mb-2">"Cost Efficiency Measures Counterbalance Increased Costs"</h5>
                      <p className="text-gray-700 text-sm">
                        In response to increased operational costs due to supply chain disruptions, JD Wetherspoon might implement cost efficiency measures. This could include renegotiating supplier contracts, finding alternative suppliers, or reducing operational inefficiencies. If successful, these measures could offset the increased costs, allowing the company to maintain its profit margins without significantly raising prices or compromising service quality.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {activeTab === 'conclusion' && (
                <div>
                  <h3 className="font-bold text-lg text-midnight-navy mb-4">Investment Conclusion</h3>
                  
                  <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-lg mb-6">
                    <h4 className="font-semibold text-midnight-navy mb-2">Recommendation: HOLD</h4>
                    <p className="text-gray-700">
                      Based on our analysis of JD Wetherspoon's situation, the complications posed by supply chain disruptions, and the evaluation of potential outcomes, we recommend a HOLD position on JDW.L stock at the current price of £631.70.
                    </p>
                  </div>
                  
                  <h4 className="font-semibold text-midnight-navy mb-3">Key Findings:</h4>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-pale-gold mr-2 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">JD Wetherspoon demonstrates resilience with 5.74% revenue growth despite industry challenges</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-pale-gold mr-2 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">Supply chain disruptions pose significant risks to operational costs and service quality</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-pale-gold mr-2 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">The company's cost efficiency measures may partially offset increased operational costs</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-pale-gold mr-2 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">Current P/E ratio of 12.37 suggests reasonable valuation relative to earnings</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-pale-gold mr-2 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">Profit margin of 2.4% indicates ongoing profitability challenges</span>
                    </li>
                  </ul>
                  
                  <p className="text-gray-700 mb-4">
                    While JD Wetherspoon faces significant challenges from supply chain disruptions, its strong market position, demonstrated revenue growth, and potential cost efficiency measures provide some resilience. However, the constrained profit margins and potential impact on service quality warrant caution.
                  </p>
                  
                  <p className="text-gray-700">
                    Investors should closely monitor the company's ability to maintain service quality and manage cost pressures in the coming quarters. Any significant deterioration in these areas could warrant a reassessment of this recommendation.
                  </p>
                </div>
              )}
            </div>
            
            <div className="p-4 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-gray-700 font-medium">JDW.L Equity Research Report</span>
                </div>
                <span className="text-gray-500 text-sm">April 26, 2025</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SampleReportSection;
